import Phaser from 'phaser';
import { GameState } from '@/engine/GameState';
import { SCENE_KEYS } from '@/engine/nav';
import { exportSaveAsJson, importSaveFromJson, persistSave } from '@/engine/storage';

const COLOR_FONDO = 0x0b0b10;
const COLOR_BOTON = 0x1f8a3b;
const COLOR_BOTON_HOVER = 0x27ab49;
// Estilo discreto para los botones de respaldo manual (sin fondo llamativo).
const COLOR_BOTON_TEXTO_DISCRETO = '#9a9aa5';
const COLOR_BOTON_TEXTO_DISCRETO_HOVER = '#ffffff';

/**
 * Pantalla de título placeholder. "LA CASA" / "ZAMBAZOKA" / botón JUGAR.
 * Todo dibujado por código (sin assets binarios), tipografía de sistema.
 */
export class TitleScene extends Phaser.Scene {
  constructor() {
    super(SCENE_KEYS.TITLE);
  }

  create(): void {
    const { width, height } = this.scale;

    this.cameras.main.setBackgroundColor(COLOR_FONDO);

    this.add
      .text(width / 2, height * 0.32, 'LA CASA', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '64px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.32 + 60, 'ZAMBAZOKA', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '26px',
        color: '#9adf9a',
        letterSpacing: 4,
      } as Phaser.Types.GameObjects.Text.TextStyle)
      .setOrigin(0.5);

    // LOGO compuesto: cohete apuntando recto arriba + hoja 🌿 saliendo de la
    // tobera COMO SI FUERA LA LLAMA (canon del grupo ZAMBAZOKA). Va entre el
    // subtítulo y el botón JUGAR, sin chocar con los de Exportar/Importar.
    const logoY = height * 0.52;
    const logoContainer = this.add.container(width / 2, logoY);

    // Cohete (emoji grande rotado -45° para que el morro apunte al cielo;
    // el cohete nativo en la mayoría de fuentes viene inclinado ~45° NE).
    const cohete = this.add
      .text(0, -45, '🚀', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '80px',
      })
      .setOrigin(0.5);
    cohete.setRotation(-Math.PI / 4);
    logoContainer.add(cohete);

    // Hoja de marihuana saliendo de la tobera (hace de llama).
    const hoja = this.add
      .text(0, 35, '🌿', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '44px',
      })
      .setOrigin(0.5);
    logoContainer.add(hoja);

    // Pulso suave de la hoja (escala + alpha, yoyo infinito).
    this.tweens.add({
      targets: hoja,
      scaleX: { from: 0.92, to: 1.18 },
      scaleY: { from: 1.18, to: 0.92 },
      alpha: { from: 1, to: 0.65 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Flotación vertical sutil del logo completo.
    this.tweens.add({
      targets: logoContainer,
      y: { from: logoY, to: logoY - 14 },
      duration: 1800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.crearBotonJugar(width / 2, height * 0.68);
    this.crearBotonesRespaldo(width / 2, height * 0.68 + 80);
  }

  private crearBotonJugar(x: number, y: number): void {
    const anchoBoton = 260;
    const altoBoton = 84;

    const boton = this.add.graphics();
    const dibujarBoton = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-anchoBoton / 2, -altoBoton / 2, anchoBoton, altoBoton, 20);
    };
    dibujarBoton(COLOR_BOTON);
    boton.setPosition(x, y);

    const texto = this.add
      .text(x, y, 'JUGAR', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '32px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0.5);

    const zona = this.add.zone(x, y, anchoBoton, altoBoton).setInteractive({ useHandCursor: true });

    zona.on('pointerover', () => dibujarBoton(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujarBoton(COLOR_BOTON));
    zona.on('pointerdown', () => dibujarBoton(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => {
      this.scene.start(SCENE_KEYS.HUB);
    });

    void texto;
  }

  /**
   * Dos botones pequeños bajo JUGAR para que el jugador de la beta pueda
   * llevarse su partida a otro dispositivo o recuperarla si se borra el
   * almacenamiento local. Ver `docs/ARQUITECTURA.md` (respaldo manual).
   */
  private crearBotonesRespaldo(x: number, y: number): void {
    const anchoBoton = 180;
    const altoBoton = 36;
    const gap = 16;

    const textos: Array<{ etiqueta: string; onUp: () => void }> = [
      {
        etiqueta: 'Exportar partida',
        onUp: () => this.exportarPartida(),
      },
      {
        etiqueta: 'Importar partida',
        onUp: () => this.importarPartida(),
      },
    ];

    const xIzq = x - anchoBoton - gap / 2;
    const xDer = x + anchoBoton + gap / 2;

    this.crearBotonDiscreto(xIzq, y, anchoBoton, altoBoton, textos[0].etiqueta, textos[0].onUp);
    this.crearBotonDiscreto(xDer, y, anchoBoton, altoBoton, textos[1].etiqueta, textos[1].onUp);
  }

  private crearBotonDiscreto(
    x: number,
    y: number,
    ancho: number,
    alto: number,
    texto: string,
    onUp: () => void,
  ): void {
    const etiqueta = this.add
      .text(x, y, texto, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: COLOR_BOTON_TEXTO_DISCRETO,
      })
      .setOrigin(0.5);

    const zona = this.add.zone(x, y, ancho, alto).setInteractive({ useHandCursor: true });

    zona.on('pointerover', () => etiqueta.setColor(COLOR_BOTON_TEXTO_DISCRETO_HOVER));
    zona.on('pointerout', () => etiqueta.setColor(COLOR_BOTON_TEXTO_DISCRETO));
    zona.on('pointerup', () => {
      etiqueta.setColor(COLOR_BOTON_TEXTO_DISCRETO_HOVER);
      onUp();
    });
  }

  /** Serializa el save actual y dispara la descarga como `la-casa-partida.json`. */
  private exportarPartida(): void {
    const save = GameState.getSnapshot() as Parameters<typeof exportSaveAsJson>[0];
    const json = exportSaveAsJson(save);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'la-casa-partida.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Pide al jugador que pegue un JSON exportado antes. Si el parser lo da
   * por bueno, lo escribimos en el espejo de guardado (IDB + localStorage)
   * y recargamos la página para que `GameState.init` arranque con ese save.
   * Si el JSON es inválido, avisamos y seguimos en la pantalla de título.
   */
  private importarPartida(): void {
    const texto = window.prompt('Pega aquí tu partida exportada');
    if (texto === null) return; // cancelado
    const save = importSaveFromJson(texto);
    if (!save) {
      window.alert('Esa partida no vale');
      return;
    }
    persistSave(save).then(() => {
      location.reload();
    });
  }
}
